/* ==========================================================================
   WaxLift — core interaction layer
   Behaviours are declared in markup via data-wl-* attributes so sections stay
   declarative and every behaviour re-binds cleanly on theme-editor reloads.
   One easing curve, transform/opacity only, all motion off under
   prefers-reduced-motion.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(pointer:fine)').matches;

  /* rAF-throttled scroll bus — one listener for the whole page */
  var scrollSubs = [];
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      for (var i = 0; i < scrollSubs.length; i++) {
        try { scrollSubs[i](); } catch (e) { /* keep the bus alive */ }
      }
      ticking = false;
    });
  }
  function subscribeScroll(fn) {
    scrollSubs.push(fn);
    fn();
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  /* Mark elements so a behaviour never binds twice.
     Kept off dataset on purpose: a key like 'Drawer' would map to
     data-wl-drawer, which is the very attribute being guarded, so an
     attribute with a value ("cart", "92") would read as already-claimed
     and the behaviour would never bind. */
  var claimed = new WeakMap();
  function claim(el, key) {
    var keys = claimed.get(el);
    if (!keys) { keys = {}; claimed.set(el, keys); }
    if (keys[key]) return false;
    keys[key] = true;
    return true;
  }

  /* ------------------------------------------------------------ reveal --- */
  function initReveal(root) {
    var els = root.querySelectorAll('.wl-rv');
    if (!els.length) return;
    if (reduced) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: .12, rootMargin: '0px 0px -40px 0px' });
    els.forEach(function (el) { if (claim(el, 'Reveal')) io.observe(el); });
  }

  /* ----------------------------------------------------------- marquee --- */
  function initMarquee(root) {
    root.querySelectorAll('[data-wl-marquee]').forEach(function (track) {
      if (!claim(track, 'Marquee')) return;
      /* Duplicate once so the -50% keyframe loops seamlessly */
      track.innerHTML += track.innerHTML;
    });
  }

  /* ----------------------------------------------------------- count up --- */
  function initCountUp(root) {
    root.querySelectorAll('[data-wl-countup]').forEach(function (el) {
      if (!claim(el, 'Count')) return;
      var target = parseFloat(el.getAttribute('data-wl-countup'));
      if (isNaN(target)) return;
      var decimals = (String(target).split('.')[1] || '').length;
      if (reduced) { el.textContent = target.toFixed(decimals); return; }
      new IntersectionObserver(function (entries, obs) {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        var t0 = performance.now();
        (function tick(ts) {
          var p = Math.min((ts - t0) / 1200, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = (target * eased).toFixed(decimals);
          if (p < 1) requestAnimationFrame(tick);
          else el.textContent = target.toFixed(decimals);
        })(t0);
      }, { threshold: .5 }).observe(el);
    });
  }

  /* --------------------------------------------------------- accordion --- */
  function initAccordion(root) {
    root.querySelectorAll('[data-wl-accordion]').forEach(function (group) {
      if (!claim(group, 'Accordion')) return;
      var single = group.getAttribute('data-wl-accordion') !== 'multi';
      group.querySelectorAll('[data-wl-accordion-trigger]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = btn.closest('[data-wl-accordion-item]');
          var panel = item.querySelector('[data-wl-accordion-panel]');
          var open = item.classList.contains('is-open');
          if (single) {
            group.querySelectorAll('[data-wl-accordion-item]').forEach(function (i) {
              i.classList.remove('is-open');
              var t = i.querySelector('[data-wl-accordion-trigger]');
              var p = i.querySelector('[data-wl-accordion-panel]');
              if (t) t.setAttribute('aria-expanded', 'false');
              if (p) p.style.maxHeight = '';
            });
          }
          if (!open) {
            item.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
            if (panel) panel.style.maxHeight = panel.scrollHeight + 'px';
          } else {
            item.classList.remove('is-open');
            btn.setAttribute('aria-expanded', 'false');
            if (panel) panel.style.maxHeight = '';
          }
        });
      });
      /* Size any item that ships open */
      group.querySelectorAll('[data-wl-accordion-item].is-open').forEach(function (i) {
        var p = i.querySelector('[data-wl-accordion-panel]');
        var t = i.querySelector('[data-wl-accordion-trigger]');
        if (t) t.setAttribute('aria-expanded', 'true');
        if (p) p.style.maxHeight = p.scrollHeight + 'px';
      });
    });
  }

  /* -------------------------------------------------------------- rail --- */
  function initRail(root) {
    root.querySelectorAll('[data-wl-rail]').forEach(function (rail) {
      if (!claim(rail, 'Rail')) return;
      var scope = rail.closest('[data-wl-rail-scope]') || root;
      var prev = scope.querySelector('[data-wl-rail-prev]');
      var next = scope.querySelector('[data-wl-rail-next]');
      function step() {
        var card = rail.firstElementChild;
        if (!card) return 320;
        var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || 22) || 22;
        return card.getBoundingClientRect().width + gap;
      }
      function go(dir) {
        rail.scrollBy({ left: dir * step(), behavior: reduced ? 'auto' : 'smooth' });
      }
      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });

      function sync() {
        var max = rail.scrollWidth - rail.clientWidth - 2;
        if (prev) prev.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= max;
      }
      rail.addEventListener('scroll', function () {
        if (rail.dataset.wlRailTick) return;
        rail.dataset.wlRailTick = '1';
        requestAnimationFrame(function () { sync(); delete rail.dataset.wlRailTick; });
      }, { passive: true });
      sync();
    });
  }

  /* ------------------------------------------------------ sticky steps --- */
  function initSteps(root) {
    var lists = root.querySelectorAll('.wl-how__steps');
    var steps = root.querySelectorAll('[data-wl-step]');
    if (!steps.length) return;

    /* Show the image belonging to a step index, if the section has one. */
    function showShot(section, i) {
      var well = section.querySelector('[data-wl-step-media]');
      if (!well) return;
      var match = well.querySelector('[data-wl-step-shot="' + i + '"]');
      /* No picture on this step: hold whatever is showing rather than
         flashing back to the base image mid-scroll. */
      if (!match) return;
      well.querySelectorAll('[data-wl-step-shot]').forEach(function (el) {
        el.classList.toggle('is-on', el === match);
      });
    }

    lists.forEach(function (list) {
      if (!claim(list, 'StepLine')) return;

      var section = list.closest('.wl-how') || document;
      var items = list.querySelectorAll('[data-wl-step]');
      if (!items.length) return;

      /* Dot centre, measured relative to the list. Read from layout rather than
         from the ::before box: getComputedStyle reports the 11px content height
         and misses the 2px border either side, and offsetTop is relative to the
         li (which is positioned), not to the list. Both errors shifted the line
         off the dots. */
      function dotY(el) {
        var cs = getComputedStyle(el, '::before');
        var top = parseFloat(cs.top) || 0;
        var h = parseFloat(cs.height) || 0;
        var bt = parseFloat(cs.borderTopWidth) || 0;
        var bb = parseFloat(cs.borderBottomWidth) || 0;
        var liTop = el.getBoundingClientRect().top - list.getBoundingClientRect().top;
        return liTop + top + (h + bt + bb) / 2;
      }

      function update() {
        var first = dotY(items[0]);
        var last = dotY(items[items.length - 1]);
        /* Progress is measured against a line 55% down the viewport: the point
           where a step reads as "the one being looked at". */
        var mark = window.innerHeight * 0.55;
        var y = mark - list.getBoundingClientRect().top;

        list.style.setProperty('--wl-how-start', first + 'px');
        var fill = Math.max(0, Math.min(y, last)) - first;
        list.style.setProperty('--wl-how-progress', Math.max(0, fill) + 'px');

        /* Light every dot the line has reached, and show the last one's image. */
        var current = -1;
        items.forEach(function (el, i) {
          var reached = y >= dotY(el);
          el.classList.toggle('is-active', reached);
          if (reached) current = i;
        });
        if (current >= 0) showShot(section, current);
      }

      subscribeScroll(update);
      update();
      /* Late-loading images change the offsets the maths depends on. */
      window.addEventListener('load', update);
    });
  }

  /* ------------------------------------------------------- word reveal --- */
  function initWords(root) {
    root.querySelectorAll('[data-wl-words]').forEach(function (el) {
      if (!claim(el, 'Words')) return;
      if (reduced) { el.classList.add('is-words-in'); return; }
      requestAnimationFrame(function () {
        setTimeout(function () { el.classList.add('is-words-in'); }, 120);
      });
    });
  }

  /* --------------------------------------------------------- ugc video --- */
  /* Plays each clip only while it is on screen: a rail of five autoplaying
     videos would compete for bandwidth and keep decoding off-screen frames.
     Controls appear once a clip is genuinely playing, so a still wall stays
     clean, and stay put afterwards so a visitor who paused can resume. */
  function initUgcVideo(root) {
    root.querySelectorAll('[data-wl-ugcvideo]').forEach(function (scope) {
      if (!claim(scope, 'UgcVideo')) return;

      var vids = scope.querySelectorAll('video');
      if (!vids.length) return;

      vids.forEach(function (v) {
        /* muted is what makes autoplay permissible; set it on the element as
           well as the attribute because some browsers ignore the attribute
           alone once the element has been adopted. */
        v.muted = true;
        v.playsInline = true;

        function showControls() {
          if (!v.controls) v.controls = true;
        }
        v.addEventListener('play', showControls);
        /* A visitor who taps the clip gets controls even if play() was blocked. */
        v.addEventListener('click', showControls);
      });

      /* Reduced motion: never start anything by itself, just expose controls. */
      if (reduced || !('IntersectionObserver' in window)) {
        vids.forEach(function (v) { v.controls = true; });
        return;
      }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting) {
            var p = v.play();
            /* Autoplay can still be refused; surface controls so the clip is
               not stranded with no way to start it. */
            if (p && p.catch) { p.catch(function () { v.controls = true; }); }
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { threshold: 0.5 });

      vids.forEach(function (v) { io.observe(v); });
    });
  }

  /* ---------------------------------------------------------- magnetic --- */
  function initMagnetic(root) {
    if (!finePointer || reduced) return;
    root.querySelectorAll('[data-wl-magnetic]').forEach(function (btn) {
      if (!claim(btn, 'Magnetic')) return;
      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        var dx = (e.clientX - r.left - r.width / 2) / (r.width / 2);
        var dy = (e.clientY - r.top - r.height / 2) / (r.height / 2);
        btn.style.transform = 'translate(' + (dx * 5).toFixed(1) + 'px,' + (dy * 4 - 2).toFixed(1) + 'px)';
      });
      btn.addEventListener('mouseleave', function () { btn.style.transform = ''; });
    });
  }

  /* ---------------------------------------------------------- parallax --- */
  function initParallax(root) {
    if (!finePointer || reduced) return;
    root.querySelectorAll('[data-wl-parallax-scope]').forEach(function (scope) {
      if (!claim(scope, 'Parallax')) return;
      var layers = scope.querySelectorAll('[data-wl-parallax-depth]');
      if (!layers.length) return;
      scope.addEventListener('mousemove', function (e) {
        var r = scope.getBoundingClientRect();
        var nx = (e.clientX - r.left) / r.width - .5;
        var ny = (e.clientY - r.top) / r.height - .5;
        layers.forEach(function (l) {
          var d = parseFloat(l.getAttribute('data-wl-parallax-depth')) || 0;
          l.style.setProperty('--wl-px', (nx * d).toFixed(2) + 'px');
          l.style.setProperty('--wl-py', (ny * d * .8).toFixed(2) + 'px');
        });
      });
      scope.addEventListener('mouseleave', function () {
        layers.forEach(function (l) {
          l.style.setProperty('--wl-px', '0px');
          l.style.setProperty('--wl-py', '0px');
        });
      });
    });
  }

  /* --------------------------------------------------------- nav + bar --- */
  function initNav(root) {
    var nav = root.querySelector('[data-wl-nav]');
    if (nav && claim(nav, 'Nav')) {
      var progress = nav.querySelector('[data-wl-progress]');
      subscribeScroll(function () {
        var max = document.documentElement.scrollHeight - window.innerHeight;
        nav.classList.toggle('is-scrolled', window.scrollY > 10);
        if (progress) progress.style.width = (max > 0 ? (window.scrollY / max) * 100 : 0) + '%';
      });
    }

    var bar = document.querySelector('[data-wl-buybar]');
    if (bar && claim(bar, 'Buybar')) {
      subscribeScroll(function () {
        var after = document.querySelector('[data-wl-buybar-after]');
        var until = document.querySelector('[data-wl-buybar-until]');
        var suppress = document.querySelector('[data-wl-buybar-suppress]');
        var past = after ? window.scrollY > after.offsetTop + after.offsetHeight - 40 : window.scrollY > 500;
        var atEnd = until ? until.getBoundingClientRect().top < window.innerHeight : false;
        var inQuiet = false;
        if (suppress) {
          var r = suppress.getBoundingClientRect();
          inQuiet = r.top < window.innerHeight - 80 && r.bottom > 80;
        }
        bar.classList.toggle('is-show', past && !atEnd && !inQuiet);
      });
    }
  }

  /* ------------------------------------------------------- canal lab --- */
  /* Scroll-scrubbed comparison: a swab compacts wax toward the eardrum while
     WaxLift grips and withdraws it. Progress 0..1 drives every element. */
  function initLab(root) {
    root.querySelectorAll('[data-wl-lab]').forEach(function (track) {
      if (!claim(track, 'Lab')) return;
      var q = function (sel) { return track.querySelector(sel); };
      var push = q('[data-wl-lab-push]'), lift = q('[data-wl-lab-lift]');
      var stBad = q('[data-wl-lab-stick-bad]'), wxBad = q('[data-wl-lab-wax-bad]'), roBad = q('[data-wl-lab-read-bad]');
      var stGood = q('[data-wl-lab-stick-good]'), wxGood = q('[data-wl-lab-wax-good]'), roGood = q('[data-wl-lab-read-good]');
      var bar = q('[data-wl-lab-bar]'), hint = q('[data-wl-lab-hint]');
      var S = (window.WaxLift && window.WaxLift.strings) || {};

      function render(p) {
        if (push) {
          push.style.opacity = p < .55 ? 1 - Math.max(0, (p - .4)) / .15 : 0;
          push.style.filter = 'blur(' + (p > .4 ? Math.min(8, (p - .4) * 60) : 0) + 'px)';
        }
        if (lift) {
          lift.style.opacity = p > .5 ? Math.min(1, (p - .5) / .15) : 0;
          lift.style.filter = 'blur(' + (p < .65 ? Math.max(0, 8 - (p - .5) * 60) : 0) + 'px)';
        }
        var pb = Math.min(1, p / .9);
        if (stBad) stBad.style.left = (2 + pb * 30) + '%';
        if (wxBad) {
          wxBad.style.left = (56 + pb * 32) + '%';
          wxBad.style.transform = 'scale(' + (1 + pb * .28) + ',' + (1 - pb * .22) + ')';
        }
        if (roBad) {
          roBad.textContent = pb < .98
            ? (S.labDepth || 'Depth __WLN__ mm').replace('__WLN__', (8 + pb * 14).toFixed(0))
            : (S.labImpacted || 'Impacted');
        }
        if (p < .5) {
          var a = p / .5;
          if (stGood) stGood.style.left = (2 + a * 26) + '%';
          if (wxGood) wxGood.style.left = '56%';
          if (roGood) roGood.textContent = S.labMovingIn || 'Adhesive head, rolling in';
        } else {
          var qq = (p - .5) / .5;
          var tipLeft = 28 - qq * 26;
          if (stGood) stGood.style.left = tipLeft + '%';
          if (wxGood) wxGood.style.left = (tipLeft + 28) + '%';
          if (roGood) roGood.textContent = qq < .9 ? (S.labGripped || 'Gripped — lifting out') : (S.labOut || 'Out. Visible on the tip');
        }
        if (bar) bar.style.width = (p * 100) + '%';
        if (hint) {
          hint.textContent = p < .05 ? (S.labStart || 'Scroll to run the comparison')
            : (p > .95 ? (S.labDone || "That's the whole difference") : (S.labScrolling || 'Keep scrolling'));
        }
      }

      if (reduced) { render(1); return; }
      subscribeScroll(function () {
        var span = track.offsetHeight - window.innerHeight;
        var p = span > 0 ? (window.scrollY - track.offsetTop) / span : 0;
        render(Math.max(0, Math.min(1, p)));
      });
    });
  }

  /* ------------------------------------------------------------- drawer --- */
  function initDrawer(root) {
    root.querySelectorAll('[data-wl-drawer]').forEach(function (drawer) {
      if (!claim(drawer, 'Drawer')) return;
      var id = drawer.getAttribute('data-wl-drawer');
      var lastFocus = null;

      function setExpanded(state) {
        document.querySelectorAll('[data-wl-drawer-open="' + id + '"]').forEach(function (b) {
          if (b.hasAttribute('aria-expanded')) b.setAttribute('aria-expanded', state);
        });
      }
      function open() {
        lastFocus = document.activeElement;
        drawer.classList.add('is-open');
        drawer.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
        setExpanded('true');
        var focusable = drawer.querySelector('[data-wl-drawer-close]');
        if (focusable) focusable.focus();
        document.addEventListener('keydown', onKey);
      }
      function close() {
        drawer.classList.remove('is-open');
        document.body.style.overflow = '';
        setExpanded('false');
        document.removeEventListener('keydown', onKey);
        setTimeout(function () { if (!drawer.classList.contains('is-open')) drawer.setAttribute('hidden', ''); }, 500);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }
      function onKey(e) {
        if (e.key === 'Escape') { close(); return; }
        if (e.key !== 'Tab') return;
        var items = drawer.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (!items.length) return;
        var first = items[0], last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }

      drawer.wlOpen = open;
      drawer.wlClose = close;
      drawer.querySelectorAll('[data-wl-drawer-close]').forEach(function (b) { b.addEventListener('click', close); });

      document.querySelectorAll('[data-wl-drawer-open="' + id + '"]').forEach(function (b) {
        if (!claim(b, 'DrawerOpen')) return;
        b.addEventListener('click', function (e) { e.preventDefault(); open(); });
      });
    });
  }

  /* --------------------------------------------------------------- money --- */
  /* Shopify money formats are Liquid templates; mirror them client-side so
     prices swapped by the pack selector match server-rendered prices exactly. */
  function formatMoney(cents) {
    var fmt = window.WaxLift && window.WaxLift.moneyFormat;
    if (!fmt) return '$' + (cents / 100).toFixed(2);
    function group(decimals, thousands, decimal) {
      var parts = (cents / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals ? parts.join(decimal) : parts[0];
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return group(2, ',', '.');
        case 'amount_no_decimals': return group(0, ',', '.');
        case 'amount_with_comma_separator': return group(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(0, '.', ',');
        case 'amount_with_space_separator': return group(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return group(0, ' ', ',');
        case 'amount_with_apostrophe_separator': return group(2, "'", '.');
        default: return group(2, ',', '.');
      }
    });
  }

  /* -------------------------------------------------------------- switch --- */
  /* One generic switcher drives the hero media chips and the use-case tabs:
     a button carries data-key, panels with the same data-key become active,
     and any data-swap-* attributes on the button are written into targets. */
  function initSwitch(root) {
    root.querySelectorAll('[data-wl-switch]').forEach(function (group) {
      if (!claim(group, 'Switch')) return;
      var buttons = group.querySelectorAll('[data-wl-switch-btn]');
      var panels = group.querySelectorAll('[data-wl-switch-panel]');

      function activate(key, btn) {
        buttons.forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
          if (b.hasAttribute('role') && b.getAttribute('role') === 'tab') b.tabIndex = on ? 0 : -1;
        });
        panels.forEach(function (p) {
          p.classList.toggle('is-on', p.getAttribute('data-wl-switch-panel') === key);
        });
        /* Copy swaps: data-swap-<targetName> on the button -> [data-wl-swap="<targetName>"] */
        Array.prototype.slice.call(btn.attributes).forEach(function (attr) {
          if (attr.name.indexOf('data-swap-') !== 0) return;
          var target = group.querySelector('[data-wl-swap="' + attr.name.slice(10) + '"]');
          if (target) target.innerHTML = attr.value;
        });
        group.setAttribute('data-wl-switch-active', key);
      }

      buttons.forEach(function (b) {
        b.addEventListener('click', function () { activate(b.getAttribute('data-wl-switch-btn'), b); });
        b.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var list = Array.prototype.slice.call(buttons);
          var i = list.indexOf(b);
          var n = list[(i + (e.key === 'ArrowRight' ? 1 : list.length - 1)) % list.length];
          n.focus();
          activate(n.getAttribute('data-wl-switch-btn'), n);
        });
      });
    });
  }

  /* ------------------------------------------------------------ hotspot --- */
  function initHotspots(root) {
    root.querySelectorAll('[data-wl-hotspot-scope]').forEach(function (scope) {
      if (!claim(scope, 'Hotspot')) return;
      var cards = scope.querySelectorAll('[data-wl-hotspot-card]');
      function closeAll() {
        cards.forEach(function (c) { c.classList.remove('is-on'); });
        scope.querySelectorAll('[data-wl-hotspot]').forEach(function (h) { h.setAttribute('aria-expanded', 'false'); });
      }
      scope.querySelectorAll('[data-wl-hotspot]').forEach(function (h) {
        h.addEventListener('click', function (e) {
          e.stopPropagation();
          var id = h.getAttribute('data-wl-hotspot');
          var card = scope.querySelector('[data-wl-hotspot-card="' + id + '"]');
          var wasOpen = card && card.classList.contains('is-on');
          closeAll();
          if (card && !wasOpen) { card.classList.add('is-on'); h.setAttribute('aria-expanded', 'true'); }
        });
      });
      scope.addEventListener('click', function (e) {
        if (!e.target.closest('[data-wl-hotspot]') && !e.target.closest('[data-wl-hotspot-card]')) closeAll();
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(); });
    });
  }


  /* ------------------------------------------------------------- gallery --- */
  /* Standard product media gallery: a stack of absolutely-positioned slides
     inside an aspect-ratio box, driven by a thumbnail tablist. The box owns the
     height, so switching media can never shift the page. */
  function initGallery(root) {
    root.querySelectorAll('[data-wl-gallery]').forEach(function (gal) {
      if (!claim(gal, 'Gallery')) return;

      var slides = gal.querySelectorAll('[data-wl-gallery-slide]');
      var thumbs = gal.querySelectorAll('[data-wl-gallery-thumb]');
      if (!slides.length) return;

      /* Index lookup so a swap knows which way to travel. */
      function indexOf(id) {
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getAttribute('data-wl-gallery-slide') === id) return i;
        }
        return -1;
      }

      var current = indexOf(
        (gal.querySelector('[data-wl-gallery-slide].is-active') || slides[0])
          .getAttribute('data-wl-gallery-slide')
      );

      function show(id) {
        var next = indexOf(id);
        if (next < 0 || next === current) return;

        /* Moving forward pushes the new slide in from the right, back from the
           left. Set before the class flip so the browser has a start position
           to animate from. */
        var forward = next > current;
        slides.forEach(function (s) {
          s.classList.toggle('is-next', forward);
          s.classList.toggle('is-prev', !forward);
        });
        current = next;

        slides.forEach(function (s) {
          var on = s.getAttribute('data-wl-gallery-slide') === id;
          s.classList.toggle('is-active', on);
          if (on) { s.removeAttribute('aria-hidden'); }
          else { s.setAttribute('aria-hidden', 'true'); }
          /* Pause any video that scrolls out of view of the eye. */
          if (!on) {
            var v = s.querySelector('video');
            if (v && !v.paused) { try { v.pause(); } catch (e) {} }
          }
        });
        thumbs.forEach(function (t) {
          var on = t.getAttribute('data-wl-gallery-thumb') === id;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
        });
      }

      thumbs.forEach(function (t, i) {
        t.addEventListener('click', function () {
          show(t.getAttribute('data-wl-gallery-thumb'));
        });
        t.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          e.preventDefault();
          var next = e.key === 'ArrowRight' ? i + 1 : i - 1;
          if (next < 0) next = thumbs.length - 1;
          if (next >= thumbs.length) next = 0;
          var target = thumbs[next];
          show(target.getAttribute('data-wl-gallery-thumb'));
          target.focus();
        });
      });

      /* Selecting a pack that has its own featured image swaps the gallery. */
      document.addEventListener('wl:variant:change', function (e) {
        if (e && e.detail && e.detail.mediaId) show(String(e.detail.mediaId));
      });
    });
  }

  /* --------------------------------------------------------------- packs --- */
  /* Variant selector. Each option carries the real Shopify variant id and
     prices in cents, so the form always posts a variant that exists. */
  function initPacks(root) {
    root.querySelectorAll('[data-wl-packs]').forEach(function (group) {
      if (!claim(group, 'Packs')) return;
      /* Price and variant mirrors live all over the page — the sticky buy bar
         and the guarantee CTA sit outside the buy card — so the selection must
         propagate document-wide, not just within the card. */
      var scope = document;
      var options = group.querySelectorAll('[data-wl-pack]');

      function select(opt) {
        options.forEach(function (o) {
          var on = o === opt;
          o.classList.toggle('is-on', on);
          var input = o.querySelector('input[type=radio]');
          if (input) input.checked = on;
          o.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        var id = opt.getAttribute('data-variant-id');
        var price = parseInt(opt.getAttribute('data-price'), 10);
        var compare = parseInt(opt.getAttribute('data-compare'), 10);
        var label = opt.getAttribute('data-pack-label') || '';
        var note = opt.getAttribute('data-pack-note') || '';

        scope.querySelectorAll('[data-wl-variant-input]').forEach(function (i) { i.value = id; });
        /* Single-variant products sell by quantity instead of by pack, so the
           option carries a quantity and the section has pre-multiplied prices. */
        var qty = opt.getAttribute('data-qty');
        if (qty) {
          scope.querySelectorAll('[data-wl-qty-input]').forEach(function (i) { i.value = qty; });
        }
        scope.querySelectorAll('[data-wl-price]').forEach(function (e) { e.textContent = formatMoney(price); });
        scope.querySelectorAll('[data-wl-compare-price]').forEach(function (e) {
          if (compare && compare > price) { e.textContent = formatMoney(compare); e.hidden = false; }
          else { e.hidden = true; }
        });
        scope.querySelectorAll('[data-wl-save]').forEach(function (e) {
          if (compare && compare > price) {
            e.textContent = String(Math.round((1 - price / compare) * 100));
            if (e.parentElement) e.parentElement.hidden = false;
          } else if (e.parentElement) { e.parentElement.hidden = true; }
        });
        scope.querySelectorAll('[data-wl-unit-price]').forEach(function (e) { e.textContent = formatMoney(price / 4); });
        scope.querySelectorAll('[data-wl-pack-label]').forEach(function (e) { e.textContent = label; });
        scope.querySelectorAll('[data-wl-pack-note]').forEach(function (e) { e.textContent = note; });
        document.dispatchEvent(new CustomEvent('wl:variant:change', {
          detail: { variantId: id, mediaId: opt.getAttribute('data-media-id') }
        }));
      }

      options.forEach(function (o) {
        o.addEventListener('click', function () { select(o); });
        o.addEventListener('keydown', function (e) {
          if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); select(o); }
        });
      });
      var initial = group.querySelector('[data-wl-pack].is-on') || options[0];
      if (initial) select(initial);
    });
  }

  /* ----------------------------------------------------------- countdown --- */
  /* Real same-day-dispatch cutoff, computed in the store's timezone. Honest
     by construction: it counts to an actual cutoff, it is not a fake timer. */
  function initCountdown(root) {
    root.querySelectorAll('[data-wl-countdown]').forEach(function (el) {
      if (!claim(el, 'Countdown')) return;
      var tz = el.getAttribute('data-wl-tz') || 'America/New_York';
      var hour = parseInt(el.getAttribute('data-wl-cutoff-hour') || '14', 10);
      var transit = parseInt(el.getAttribute('data-wl-transit-days') || '4', 10);
      var h = el.querySelector('[data-wl-cd-h]');
      var m = el.querySelector('[data-wl-cd-m]');
      var s = el.querySelector('[data-wl-cd-s]');
      var combo = el.querySelectorAll('[data-wl-cd-combined]');
      var dates = document.querySelectorAll('[data-wl-delivery-date]');

      function pad(n) { return String(n).padStart(2, '0'); }
      function tick() {
        var now = new Date();
        var local = new Date(now.toLocaleString('en-US', { timeZone: tz }));
        var cutoff = new Date(local);
        cutoff.setHours(hour, 0, 0, 0);
        var shipsToday = local < cutoff;
        if (!shipsToday) cutoff.setDate(cutoff.getDate() + 1);
        var ms = cutoff - local;
        var total = Math.max(0, Math.floor(ms / 1000));
        var hh = Math.floor(total / 3600), mm = Math.floor((total % 3600) / 60), ss = total % 60;
        if (h) h.textContent = pad(hh);
        if (m) m.textContent = pad(mm);
        if (s) s.textContent = pad(ss);
        combo.forEach(function (c) { c.textContent = pad(hh) + ':' + pad(mm) + ':' + pad(ss); });

        var ship = new Date(local);
        if (!shipsToday) ship.setDate(ship.getDate() + 1);
        var arrive = new Date(ship);
        arrive.setDate(arrive.getDate() + transit);
        var label = arrive.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
        dates.forEach(function (d) { d.textContent = label; });
      }
      tick();
      setInterval(tick, 1000);
    });
  }

  /* ------------------------------------------------------------- compare --- */
  function initCompare(root) {
    root.querySelectorAll('[data-wl-compare]').forEach(function (el) {
      if (!claim(el, 'Compare')) return;
      var pane = el.querySelector('[data-wl-compare-pane]');
      var handle = el.querySelector('[data-wl-compare-handle]');
      var pct = 50;

      function apply(p) {
        pct = Math.max(0, Math.min(100, p));
        if (pane) pane.style.clipPath = 'inset(0 0 0 ' + pct + '%)';
        if (handle) handle.style.left = pct + '%';
        el.setAttribute('aria-valuenow', Math.round(pct));
      }
      function fromEvent(e) {
        var r = el.getBoundingClientRect();
        var x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
        apply((x / r.width) * 100);
      }
      var dragging = false;
      el.addEventListener('pointerdown', function (e) {
        dragging = true;
        el.setPointerCapture && el.setPointerCapture(e.pointerId);
        fromEvent(e);
      });
      el.addEventListener('pointermove', function (e) { if (dragging) fromEvent(e); });
      el.addEventListener('pointerup', function () { dragging = false; });
      el.addEventListener('pointercancel', function () { dragging = false; });
      el.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); apply(pct - 4); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); apply(pct + 4); }
        else if (e.key === 'Home') { e.preventDefault(); apply(0); }
        else if (e.key === 'End') { e.preventDefault(); apply(100); }
      });
      apply(50);
    });
  }

  /* ---------------------------------------------------------------- ring --- */
  function initRings(root) {
    root.querySelectorAll('[data-wl-ring]').forEach(function (el) {
      if (!claim(el, 'Ring')) return;
      var target = parseFloat(el.getAttribute('data-wl-ring')) || 0;
      var out = el.querySelector('[data-wl-ring-value]');
      function paint(v) {
        el.style.setProperty('--wl-ring-p', v);
        if (out) out.textContent = Math.round(v) + '%';
      }
      if (reduced) { paint(target); return; }
      paint(0);
      new IntersectionObserver(function (entries, obs) {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        var t0 = performance.now();
        (function tick(ts) {
          var p = Math.min((ts - t0) / 1400, 1);
          paint(target * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(tick);
          else paint(target);
        })(t0);
      }, { threshold: .4 }).observe(el);
    });
  }

  /* ------------------------------------------------------------ validate --- */
  /* Inline form validation. The browser's own validation is left on as the
     fallback (no novalidate until we know JS is running), so a failed script
     degrades to native messages rather than to no validation at all. */
  function initValidate(root) {
    root.querySelectorAll('[data-wl-validate]').forEach(function (form) {
      if (!claim(form, 'Validate')) return;
      form.setAttribute('novalidate', 'novalidate');
      var fields = form.querySelectorAll('[data-wl-field]');

      function check(field) {
        var input = field.querySelector('input, textarea, select');
        if (!input) return true;
        var rule = field.getAttribute('data-wl-field') || '';
        var value = (input.value || '').trim();
        var ok = true;
        if (rule.indexOf('required') > -1 && !value) ok = false;
        if (ok && rule.indexOf('email') > -1 && value && !/.+@.+\..+/.test(value)) ok = false;
        var min = rule.match(/min:(\d+)/);
        if (ok && min && value.length < parseInt(min[1], 10)) ok = false;
        field.classList.toggle('is-bad', !ok);
        var err = field.querySelector('[data-wl-error]');
        if (err) err.hidden = ok;
        input.setAttribute('aria-invalid', ok ? 'false' : 'true');
        return ok;
      }

      fields.forEach(function (f) {
        var input = f.querySelector('input, textarea, select');
        if (!input) return;
        input.addEventListener('blur', function () { check(f); });
        input.addEventListener('input', function () {
          if (f.classList.contains('is-bad')) check(f);
        });
      });

      form.addEventListener('submit', function (e) {
        var firstBad = null;
        fields.forEach(function (f) {
          if (!check(f) && !firstBad) firstBad = f;
        });
        if (firstBad) {
          e.preventDefault();
          var input = firstBad.querySelector('input, textarea, select');
          if (input) input.focus();
        }
      });
    });
  }

  /* --------------------------------------------------------------- init --- */
  /* Each behaviour is isolated: a section that is absent, or markup that is
     malformed, must not stop the rest of the page from binding. */
  var BEHAVIOURS = [
    ['reveal', initReveal], ['marquee', initMarquee], ['countUp', initCountUp],
    ['accordion', initAccordion], ['rail', initRail], ['steps', initSteps],
    ['words', initWords], ['magnetic', initMagnetic], ['parallax', initParallax],
    ['nav', initNav], ['lab', initLab], ['drawer', initDrawer],
    ['switch', initSwitch], ['hotspots', initHotspots], ['packs', initPacks],
    ['gallery', initGallery],
    ['countdown', initCountdown], ['compare', initCompare], ['rings', initRings],
    ['ugcVideo', initUgcVideo],
    ['validate', initValidate]
  ];

  function initAll(root) {
    root = root || document;
    for (var i = 0; i < BEHAVIOURS.length; i++) {
      try {
        BEHAVIOURS[i][1](root);
      } catch (e) {
        if (window.console && console.warn) {
          console.warn('WaxLift: ' + BEHAVIOURS[i][0] + ' failed to initialise', e);
        }
      }
    }
  }

  window.WaxLift = window.WaxLift || {};
  window.WaxLift.init = initAll;
  window.WaxLift.reduced = reduced;
  window.WaxLift.formatMoney = formatMoney;
  window.WaxLift.openDrawer = function (id) {
    var d = document.querySelector('[data-wl-drawer="' + id + '"]');
    if (d && d.wlOpen) d.wlOpen();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Theme editor: re-bind whatever the editor swapped in */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initAll(e.target); });
})();
